// The Schedule of Work's cover note, written from the areas — Site2Scope's
// generateCoverNote (ScopePDFPreview.js:23-114), adjusted for the client
// (docs/SCHEDULE-OF-WORK.md). The estimator can edit it before generating. Pure.

interface CoverNoteArea {
  name: string;
  side: string | null;
  scopeNotes: string | null;
  exclusions: string | null;
  defects: string | null;
}

export interface CoverNoteInput {
  site: string | null;
  /** DD/MM/YYYY. */
  date: string;
  estimator: string | null;
  areas: CoverNoteArea[];
  /** null for "Scope only": the hours sentence is left out. */
  totals: { hours: number; litres: number } | null;
}

const CLOSING = 'All measurements are approximate and subject to confirmation on commencement.';

const names = (areas: CoverNoteArea[]) => areas.map((a) => a.name).join(', ');
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
const has = (text: string | null) => Boolean(text && text.trim());

function scopeType(internal: number, external: number): string {
  if (internal && external) return 'internal and external painting works';
  if (internal) return 'internal painting works';
  if (external) return 'external painting works';
  return 'painting works';
}

const internalOf = (areas: CoverNoteArea[]) => areas.filter((a) => a.side === 'Internal');
const externalOf = (areas: CoverNoteArea[]) => areas.filter((a) => a.side !== 'Internal');

/** A heading line, then one "• " line per item: the cover draws the heading bold and the items as bullets. */
const bulleted = (heading: string, items: string[]) => `${heading}\n${items.map((item) => `• ${item}`).join('\n')}`;

function opening({ site, date, estimator, areas }: CoverNoteInput): string {
  const where = site ? ` at ${site}` : '';
  const by = estimator ? ` by ${estimator}` : '';
  return `Site assessment for ${scopeType(internalOf(areas).length, externalOf(areas).length)}${where}, completed ${date}${by}.`;
}

/** The areas under Internal and External headings, one bullet each, in the body's order (DD, 2026-10-26). */
function areaLists(areas: CoverNoteArea[]): string[] {
  const lists: [string, CoverNoteArea[]][] = [['Internal', internalOf(areas)], ['External', externalOf(areas)]];
  return lists.filter(([, list]) => list.length).map(([heading, list]) => bulleted(heading, list.map((a) => a.name)));
}

/** The areas' scope notes, each once. Labelled as what they are; the paint systems are the option's (cover details, #412; DD, 2026-10-26). */
function scopeNotes(areas: CoverNoteArea[]): string {
  const notes = [...new Set(areas.map((a) => a.scopeNotes?.trim() ?? '').filter(Boolean))];
  if (notes.length === 0) return '';
  if (notes.length === 1) return `Scope notes: ${notes[0]}`;
  return bulleted('Scope notes:', notes);
}

export function coverNote(input: CoverNoteInput): string {
  const { areas, totals } = input;
  const excluded = areas.filter((a) => has(a.exclusions));
  const defects = areas.filter((a) => has(a.defects));
  const paragraphs = [
    opening(input),
    ...areaLists(areas),
    totals && totals.hours > 0
      ? `Estimated ${Math.round(totals.hours)} hours total labour and ${Math.round(totals.litres)} litres of paint.`
      : '',
    scopeNotes(areas),
    excluded.length ? `Exclusions apply to ${plural(excluded.length, 'area')} (${names(excluded)}) — see each area below.` : '',
    defects.length ? `Defects or remediation noted in ${plural(defects.length, 'area')} (${names(defects)}) — review before commencing.` : '',
    CLOSING,
  ];
  return paragraphs.filter(Boolean).join('\n\n');
}
